import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useHomepage } from '@/contexts/homepage-context';
import {
  IMPLICIT_PRIMITIVE_LABELS,
  GyroidSphereParams,
  SuperEllipsoidParams,
  LatticePipeParams,
  GyroidGenusParams,
  BaseBoxParams,
  BaseSphereParams,
  BaseCylinderParams,
  BasePipeParams,
  BasePipeSegmentParams,
  BaseRingParams,
  BaseLensParams,
  LatticeManifoldParams,
  RoverWheelParams,
  HelixHeatXParams,
  NasaLatticeParams,
} from '@/config/implicitPrimitives';

// Sidebar params for the active Implicit Primitive. The active primitive is
// chosen from the TopToolbar dropdown; this panel renders whichever set of
// numeric knobs that primitive exposes. Defaults are populated up in
// HomepageContext / TopToolbar — by the time we render, params are always
// valid, so the inputs never start empty.
export function ImplicitPrimitivesContent({ enabled }: { enabled: boolean }) {
  const {
    implicitPrimitive,
    implicitPrimitiveParams,
    setImplicitPrimitiveParams,
    bumpDomainParams,
  } = useHomepage();

  const numField = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    unit: string,
  ) => (
    <div className="flex items-center gap-2">
      <span className="flex-1 text-xs text-telekinesis-neutral-50">
        {label}
      </span>
      <Input
        type="number"
        step="any"
        disabled={!enabled}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          bumpDomainParams();
        }}
        className="h-7 w-20 rounded-md px-2 text-center text-xs text-[#107d40]"
      />
      <span className="w-12 text-right text-xs text-telekinesis-neutral-50">
        {unit}
      </span>
    </div>
  );

  // Defensive narrowing: the params discriminator must agree with the active
  // primitive, but they're set in lockstep up in TopToolbar so this is
  // belt-and-braces. If they ever disagree we render nothing for the panel.
  let body: React.ReactNode = null;

  if (
    implicitPrimitive === 'gyroid-sphere' &&
    implicitPrimitiveParams.kind === 'gyroid-sphere'
  ) {
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<GyroidSphereParams>) =>
      setImplicitPrimitiveParams({
        kind: 'gyroid-sphere',
        params: { ...p, ...patch },
      });
    body = (
      <>
        {numField('Radius', p.radius, (v) => set({ radius: v }), '[mm]')}
        {numField('Gyroid Period', p.period, (v) => set({ period: v }), '[-]')}
        {numField('Gyroid Ratio', p.ratio, (v) => set({ ratio: v }), '[-]')}
      </>
    );
  } else if (
    implicitPrimitive === 'super-ellipsoid' &&
    implicitPrimitiveParams.kind === 'super-ellipsoid'
  ) {
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<SuperEllipsoidParams>) =>
      setImplicitPrimitiveParams({
        kind: 'super-ellipsoid',
        params: { ...p, ...patch },
      });
    body = (
      <>
        {numField('Ax', p.ax, (v) => set({ ax: v }), '[mm]')}
        {numField('Ay', p.ay, (v) => set({ ay: v }), '[mm]')}
        {numField('Az', p.az, (v) => set({ az: v }), '[mm]')}
        {numField(
          'Epsilon 1 (n)',
          p.epsilon1,
          (v) => set({ epsilon1: v }),
          '[-]',
        )}
        {numField(
          'Epsilon 2 (e)',
          p.epsilon2,
          (v) => set({ epsilon2: v }),
          '[-]',
        )}
      </>
    );
  } else if (
    implicitPrimitive === 'lattice-pipe' &&
    implicitPrimitiveParams.kind === 'lattice-pipe'
  ) {
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<LatticePipeParams>) =>
      setImplicitPrimitiveParams({
        kind: 'lattice-pipe',
        params: { ...p, ...patch },
      });
    body = (
      <>
        {numField('Length', p.length, (v) => set({ length: v }), '[mm]')}
        {numField('Beam Radius', p.radius, (v) => set({ radius: v }), '[mm]')}
      </>
    );
  } else if (
    implicitPrimitive === 'gyroid-genus' &&
    implicitPrimitiveParams.kind === 'gyroid-genus'
  ) {
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<GyroidGenusParams>) =>
      setImplicitPrimitiveParams({
        kind: 'gyroid-genus',
        params: { ...p, ...patch },
      });
    body = (
      <>
        {numField('Gap', p.gap, (v) => set({ gap: v }), '[mm]')}
        {numField('Extent', p.extent, (v) => set({ extent: v }), '[mm]')}
        {numField('Gyroid Period', p.period, (v) => set({ period: v }), '[-]')}
        {numField('Gyroid Ratio', p.ratio, (v) => set({ ratio: v }), '[-]')}
      </>
    );
  } else if (
    implicitPrimitive === 'base-box' &&
    implicitPrimitiveParams.kind === 'base-box'
  ) {
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<BaseBoxParams>) =>
      setImplicitPrimitiveParams({
        kind: 'base-box',
        params: { ...p, ...patch },
      });
    body = (
      <>
        {numField('Height', p.height, (v) => set({ height: v }), '[mm]')}
        {numField('Width', p.width, (v) => set({ width: v }), '[mm]')}
        {numField('Depth', p.depth, (v) => set({ depth: v }), '[mm]')}
      </>
    );
  } else if (
    implicitPrimitive === 'base-sphere' &&
    implicitPrimitiveParams.kind === 'base-sphere'
  ) {
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<BaseSphereParams>) =>
      setImplicitPrimitiveParams({
        kind: 'base-sphere',
        params: { ...p, ...patch },
      });
    body = (
      <>{numField('Radius', p.radius, (v) => set({ radius: v }), '[mm]')}</>
    );
  } else if (
    implicitPrimitive === 'base-cylinder' &&
    implicitPrimitiveParams.kind === 'base-cylinder'
  ) {
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<BaseCylinderParams>) =>
      setImplicitPrimitiveParams({
        kind: 'base-cylinder',
        params: { ...p, ...patch },
      });
    body = (
      <>
        {numField('Length', p.length, (v) => set({ length: v }), '[mm]')}
        {numField('Radius', p.radius, (v) => set({ radius: v }), '[mm]')}
      </>
    );
  } else if (
    implicitPrimitive === 'base-pipe' &&
    implicitPrimitiveParams.kind === 'base-pipe'
  ) {
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<BasePipeParams>) =>
      setImplicitPrimitiveParams({
        kind: 'base-pipe',
        params: { ...p, ...patch },
      });
    body = (
      <>
        {numField('Length', p.length, (v) => set({ length: v }), '[mm]')}
        {numField(
          'Inner Radius',
          p.innerRadius,
          (v) => set({ innerRadius: v }),
          '[mm]',
        )}
        {numField(
          'Outer Radius',
          p.outerRadius,
          (v) => set({ outerRadius: v }),
          '[mm]',
        )}
      </>
    );
  } else if (
    implicitPrimitive === 'base-pipe-segment' &&
    implicitPrimitiveParams.kind === 'base-pipe-segment'
  ) {
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<BasePipeSegmentParams>) =>
      setImplicitPrimitiveParams({
        kind: 'base-pipe-segment',
        params: { ...p, ...patch },
      });
    body = (
      <>
        {numField('Length', p.length, (v) => set({ length: v }), '[mm]')}
        {numField(
          'Inner Radius',
          p.innerRadius,
          (v) => set({ innerRadius: v }),
          '[mm]',
        )}
        {numField(
          'Outer Radius',
          p.outerRadius,
          (v) => set({ outerRadius: v }),
          '[mm]',
        )}
        {numField('φ Mid', p.phiMid, (v) => set({ phiMid: v }), '[rad]')}
        {numField('φ Range', p.phiRange, (v) => set({ phiRange: v }), '[rad]')}
      </>
    );
  } else if (
    implicitPrimitive === 'base-ring' &&
    implicitPrimitiveParams.kind === 'base-ring'
  ) {
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<BaseRingParams>) =>
      setImplicitPrimitiveParams({
        kind: 'base-ring',
        params: { ...p, ...patch },
      });
    body = (
      <>
        {numField(
          'Ring Radius',
          p.ringRadius,
          (v) => set({ ringRadius: v }),
          '[mm]',
        )}
        {numField(
          'Tube Radius',
          p.tubeRadius,
          (v) => set({ tubeRadius: v }),
          '[mm]',
        )}
      </>
    );
  } else if (
    implicitPrimitive === 'base-lens' &&
    implicitPrimitiveParams.kind === 'base-lens'
  ) {
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<BaseLensParams>) =>
      setImplicitPrimitiveParams({
        kind: 'base-lens',
        params: { ...p, ...patch },
      });
    body = (
      <>
        {numField('Height 1', p.height1, (v) => set({ height1: v }), '[mm]')}
        {numField('Height 2', p.height2, (v) => set({ height2: v }), '[mm]')}
        {numField('Radius', p.radius, (v) => set({ radius: v }), '[mm]')}
      </>
    );
  } else if (
    implicitPrimitive === 'lattice-manifold' &&
    implicitPrimitiveParams.kind === 'lattice-manifold'
  ) {
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<LatticeManifoldParams>) =>
      setImplicitPrimitiveParams({
        kind: 'lattice-manifold',
        params: { ...p, ...patch },
      });
    const both = p.extendBothSides === 'true';
    body = (
      <>
        {numField('Length', p.length, (v) => set({ length: v }), '[mm]')}
        {numField('Radius', p.radius, (v) => set({ radius: v }), '[mm]')}
        {numField(
          'Overhang Angle',
          p.overhangAngle,
          (v) => set({ overhangAngle: v }),
          '[°]',
        )}
        {/* Boolean toggle — surfaced as a checkbox so the user doesn't have
            to type 'true' / 'false'. Stored in params as a stringified bool
            for parity with the rest of the param map shape. */}
        <div className="flex items-center gap-2 pt-1">
          <label className="flex-1 text-xs text-telekinesis-neutral-50">
            Extend Both Sides
          </label>
          <input
            type="checkbox"
            disabled={!enabled}
            checked={both}
            onChange={(e) => {
              set({ extendBothSides: e.target.checked ? 'true' : 'false' });
              bumpDomainParams();
            }}
            className="h-4 w-4"
          />
          <span className="w-12 text-right text-xs text-telekinesis-neutral-50">
            {both ? 'on' : 'off'}
          </span>
        </div>
      </>
    );
  } else if (
    implicitPrimitive === 'helix-heat-x' &&
    implicitPrimitiveParams.kind === 'helix-heat-x'
  ) {
    // The helix "feature tree": geometry + pipeline knobs, all tunable in one
    // generate so their fidelity/build-time impact can be measured. Defaults are
    // tutorial / full-fidelity. Finer voxel + smaller sampling step = crisper but
    // much slower; the download is lossless regardless of preview budget.
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<HelixHeatXParams>) =>
      setImplicitPrimitiveParams({
        kind: 'helix-heat-x',
        params: { ...p, ...patch },
      });
    body = (
      <>
        {numField(
          'Plate Thickness',
          p.plateThickness,
          (v) => set({ plateThickness: v }),
          '[mm]',
        )}
        {numField(
          'Wall Thickness',
          p.wallThickness,
          (v) => set({ wallThickness: v }),
          '[mm]',
        )}
        {numField('IO Radius', p.ioRadius, (v) => set({ ioRadius: v }), '[mm]')}
        {numField(
          'Voxel Size',
          p.voxelSize,
          (v) => set({ voxelSize: v }),
          '[mm]',
        )}
        {numField(
          'Sampling Step',
          p.samplingStep,
          (v) => set({ samplingStep: v }),
          '[mm]',
        )}
        {numField(
          'Preview Budget',
          p.previewBudget,
          (v) => set({ previewBudget: v }),
          '[tris]',
        )}
        <div className="flex items-center gap-2">
          <span className="flex-1 text-xs text-telekinesis-neutral-50">
            Transport
          </span>
          <select
            disabled={!enabled}
            value={p.transport}
            onChange={(e) => {
              set({ transport: e.target.value });
              bumpDomainParams();
            }}
            className="h-7 w-32 rounded-md bg-telekinesis-bg-dark px-2 text-center text-xs text-[#107d40]"
          >
            <option value="glb">GLB (progressive)</option>
            <option value="full">Full STL (lossless)</option>
            <option value="tiers">Tiers (decimated)</option>
          </select>
        </div>
      </>
    );
  } else if (
    implicitPrimitive === 'rover-wheel' &&
    implicitPrimitiveParams.kind === 'rover-wheel'
  ) {
    const p = implicitPrimitiveParams.params;
    const set = (patch: Partial<RoverWheelParams>) =>
      setImplicitPrimitiveParams({
        kind: 'rover-wheel',
        params: { ...p, ...patch },
      });
    // Preset list — ordered per user spec (default first, then by layer count
    // and tread family). Each entry carries the full apply-on-pick payload:
    //   value     = backend `variant` (1-4 = preset Wheel_NN, 0 = RandomWheel)
    //   label     = full visible string in both the trigger and the menu
    //   dims      = the wheel's hardcoded class-field dimensions (LEAP71
    //               Wheel_NN.cs constructors all use 120/30/60; the random
    //               wheel rolls its own each generate, so dims is null).
    //   tread     = the tread-pattern enum value to mirror into the Tread
    //               Pattern dropdown when this preset is picked.
    type PresetDims = { outerRadius: string; hubRadius: string; width: string };
    type Preset = {
      value: string;
      label: string;
      dims: PresetDims | null;
      tread: string;
    };
    const PRESETS: Preset[] = [
      {
        value: '2',
        label: '5 Layers Profiled, Diamond Tread',
        dims: { outerRadius: '120', hubRadius: '30', width: '60' },
        tread: 'diamond',
      },
      {
        value: '3',
        label: '3 Layers Profiled, Diamond Tread',
        dims: { outerRadius: '120', hubRadius: '30', width: '60' },
        tread: 'diamond',
      },
      {
        value: '1',
        label: '5 Layer Solid, Chevron Tread',
        dims: { outerRadius: '120', hubRadius: '30', width: '60' },
        tread: 'chevron',
      },
      {
        value: '4',
        label: '3 Layers Profiled, Chevron Tread',
        dims: { outerRadius: '120', hubRadius: '30', width: '60' },
        tread: 'chevron',
      },
      // Random doesn't carry dims — every Generate produces a different wheel
      // and the C# RandomWheel rolls its own outer/hub/width each call. Picking
      // it clears the parameter inputs so the user doesn't see stale numbers
      // attached to an unpredictable output.
      { value: '0', label: 'Random Seed', dims: null, tread: '' },
    ];
    const TREAD_PATTERNS = [
      { value: 'diamond', label: 'Diamond Tread' },
      { value: 'chevron', label: 'Chevron Tread' },
      { value: 'knob', label: 'Knob Tread' },
    ];
    const activePreset = PRESETS.find((preset) => preset.value === p.variant);
    const isRandom = p.variant === '0';
    // Picking a preset rewrites variant + dimensions + tread atomically so the
    // visible parametric inputs match what the backend will generate. Random
    // is the exception — it clears dims/tread because the random wheel doesn't
    // have a knowable set of dimensions until the C# code rolls them.
    const applyPreset = (preset: Preset) => {
      set({
        variant: preset.value,
        outerRadius: preset.dims?.outerRadius ?? '',
        hubRadius: preset.dims?.hubRadius ?? '',
        width: preset.dims?.width ?? '',
        treadPattern: preset.tread,
      });
      bumpDomainParams();
    };
    body = (
      <>
        <Select
          disabled={!enabled}
          value={p.variant}
          onValueChange={(v) => {
            const picked = PRESETS.find((preset) => preset.value === v);
            if (picked) applyPreset(picked);
          }}
        >
          <SelectTrigger className="h-8 w-full rounded-md border border-telekinesis-neutral-700 bg-telekinesis-neutral-800 px-2.5 text-xs text-telekinesis-neutral-50 hover:bg-telekinesis-neutral-700 focus:ring-0 focus:ring-offset-0">
            <SelectValue placeholder="Preset Configuration">
              {activePreset?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="border-telekinesis-neutral-700 bg-telekinesis-neutral-800 text-telekinesis-neutral-50">
            <SelectGroup>
              <SelectLabel className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-telekinesis-neutral-400">
                Preset Configuration
              </SelectLabel>
              {PRESETS.map((preset) => (
                <SelectItem
                  key={preset.value}
                  value={preset.value}
                  className="text-xs focus:bg-[#107d40]/20 focus:text-telekinesis-neutral-50 data-[state=checked]:bg-telekinesis-neutral-700"
                >
                  {preset.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        {isRandom && (
          <>
            {numField('Seed', p.seed, (v) => set({ seed: v }), '')}
            <p className="text-[10px] leading-snug text-telekinesis-neutral-400">
              Note: the seed is currently a no-op upstream — LEAP71's
              RandomWheel doesn't actually seed its RNG. See{' '}
              <a
                href="https://github.com/tk-founder/telekinesis/issues/37"
                target="_blank"
                rel="noreferrer"
                className="text-[#107d40] underline"
              >
                #37
              </a>
              .
            </p>
          </>
        )}

        {numField(
          'Outer Radius',
          p.outerRadius,
          (v) => set({ outerRadius: v }),
          '[mm]',
        )}
        {numField(
          'Hub Radius',
          p.hubRadius,
          (v) => set({ hubRadius: v }),
          '[mm]',
        )}
        {numField('Width', p.width, (v) => set({ width: v }), '[mm]')}

        <Select
          disabled={!enabled}
          value={p.treadPattern}
          onValueChange={(v) => {
            set({ treadPattern: v });
            bumpDomainParams();
          }}
        >
          <SelectTrigger className="h-8 w-full rounded-md border border-telekinesis-neutral-700 bg-telekinesis-neutral-800 px-2.5 text-xs text-telekinesis-neutral-50 hover:bg-telekinesis-neutral-700 focus:ring-0 focus:ring-offset-0">
            <SelectValue placeholder="Tread Pattern" />
          </SelectTrigger>
          <SelectContent className="border-telekinesis-neutral-700 bg-telekinesis-neutral-800 text-telekinesis-neutral-50">
            <SelectGroup>
              <SelectLabel className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-telekinesis-neutral-400">
                Tread Pattern
              </SelectLabel>
              {TREAD_PATTERNS.map((t) => (
                <SelectItem
                  key={t.value}
                  value={t.value}
                  className="text-xs focus:bg-[#107d40]/20 focus:text-telekinesis-neutral-50 data-[state=checked]:bg-telekinesis-neutral-700"
                >
                  {t.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </>
    );
  } else if (
    implicitPrimitive.startsWith('nasa-lattice-') &&
    implicitPrimitiveParams.kind === 'nasa-lattice'
  ) {
    // All 14 NASA Unitcell topologies share the same (length, width, height,
    // thickness) param shape per the engine's UnitcellDesign(name, L, W, H,
    // thickness=...) signature. Bounds match the Pydantic model in the
    // Python sidecar — keep these in sync; mismatches surface as 422 on
    // the /unitcell call.
    const p = implicitPrimitiveParams.params;
    const topology = implicitPrimitiveParams.topology;
    const set = (patch: Partial<NasaLatticeParams>) =>
      setImplicitPrimitiveParams({
        kind: 'nasa-lattice',
        topology,
        params: { ...p, ...patch },
      });
    body = (
      <>
        {numField('Length (L)', p.length, (v) => set({ length: v }), '[-]')}
        {numField('Width (W)', p.width, (v) => set({ width: v }), '[-]')}
        {numField('Height (H)', p.height, (v) => set({ height: v }), '[-]')}
        {numField(
          'Thickness (t/L)',
          p.thickness,
          (v) => set({ thickness: v }),
          '[-]',
        )}
      </>
    );
  }

  return (
    <div>
      <p className="mb-2 text-xs font-semibold text-telekinesis-neutral-50">
        {IMPLICIT_PRIMITIVE_LABELS[implicitPrimitive]}
      </p>
      <div className="space-y-3">{body}</div>
    </div>
  );
}
